import { isOtherCompositionBucket } from '@revelio/core/composition-buckets';
import type { CompositionV3DataQuery } from '@revelio/data-access';

import type { TreemapDrilldownSegment } from '../../../filters/utils/treemapDrilldownLevels';
import {
  type CompositionV3FullMetrics,
  isCompositionV3FullMetrics,
} from './composition-v3/composition-v3-graphql-response';
import { pointAtSnapshot } from './composition-v3/convert-composition-data-v3';

type CompositionV3Payload = NonNullable<
  CompositionV3DataQuery['composition_v3']
>;

type SegmentBucket = NonNullable<
  CompositionV3FullMetrics[TreemapDrilldownSegment]
>[number];

/**
 * Keeps the largest cells of every drawn group out of a breakdown that spans
 * the whole population. `drawnGroupOf` names the group a cell is drawn in, or
 * null when its group is not drawn.
 */
export function keepTopDrawnCells(
  data: CompositionV3Payload,
  segment: TreemapDrilldownSegment,
  limitPerGroup: number,
  drawnGroupOf: (bucketId: string | number) => string | null
): CompositionV3Payload {
  return {
    ...data,
    entities: (data.entities ?? []).map((entity) => {
      const metrics = isCompositionV3FullMetrics(entity.metrics)
        ? entity.metrics
        : undefined;
      const buckets = metrics?.[segment];
      if (metrics === undefined || !Array.isArray(buckets)) return entity;
      const snapshotId = metrics.headcount[metrics.headcount.length - 1]?.id;
      const countOf = (bucket: SegmentBucket) =>
        pointAtSnapshot(bucket.headcount, snapshotId)?.count ?? 0;
      const keptPerGroup = new Map<string, number>();
      const kept = new Set<SegmentBucket>();
      for (const bucket of [...buckets].sort(
        (a, b) => countOf(b) - countOf(a)
      )) {
        if (isOtherCompositionBucket(bucket.id)) continue;
        const group = drawnGroupOf(bucket.id);
        if (group === null) continue;
        const keptInGroup = keptPerGroup.get(group) ?? 0;
        if (keptInGroup >= limitPerGroup) continue;
        keptPerGroup.set(group, keptInGroup + 1);
        kept.add(bucket);
      }
      return {
        ...entity,
        metrics: {
          ...metrics,
          [segment]: buckets.filter(
            (bucket) => kept.has(bucket) || isOtherCompositionBucket(bucket.id)
          ),
        },
      };
    }),
  };
}
